/* ==========================================================================
 * Admin 的 `X-Admin-Token`（DESIGN-admin §5.3）
 *
 * 為什麼是 `sessionStorage` 而不是 localStorage／config.json：
 * - `config.json` 是 Pages 上的公開檔，放進去等於公告（§5.3 明確禁止）。
 * - `sessionStorage` 關掉分頁就消失，換一台機器／換個人用都拿不到；
 *   代價是重新整理分頁還在、關掉要重打一次。
 *
 * key 固定為 `chat-rea.admin.token`，只改這一個常數（前後的讀寫都走本檔的函式）。
 * ========================================================================== */
/** Admin 金鑰在 `sessionStorage` 的 key。 */
export const ADMIN_TOKEN_STORAGE_KEY = 'chat-rea.admin.token';
/**
 * sessionStorage 在無痕模式／封鎖網站資料時會**直接 throw**（不是回 null）；
 * 拿不到就當「沒有存」，不要讓整個控制台因為讀不到儲存空間而白畫面。
 */
function adminStorage() {
    try {
        if (typeof window === 'undefined')
            return null;
        return window.sessionStorage;
    }
    catch {
        return null;
    }
}
/** 目前這台瀏覽器記住的 token；沒有就是空字串（不拋錯）。 */
export function readAdminToken() {
    try {
        return (adminStorage()?.getItem(ADMIN_TOKEN_STORAGE_KEY) ?? '').trim();
    }
    catch {
        return '';
    }
}
/** 存入 token；傳空字串＝清除。 */
export function writeAdminToken(token) {
    const value = token.trim();
    try {
        const storage = adminStorage();
        if (!storage)
            return;
        if (value === '')
            storage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
        else
            storage.setItem(ADMIN_TOKEN_STORAGE_KEY, value);
    }
    catch {
        // 存不進去就是「這次分頁有效、重整後要再打一次」，不影響已經拿到的請求。
    }
}
/** 這個錯誤是不是「admin token 沒帶／不對」？（`http-client` 會把 HTTP 狀態掛在 `status`） */
export function isUnauthorizedError(err) {
    const status = err?.status;
    return status === 401 || status === 403;
}
/* ==========================================================================
 * 登入過期通知（PIN 登入簽發的 Bearer session，見 DECISIONS 0049）
 *
 * `/api/*` 一律要 session（`Authorization: Bearer`），過期／撤銷時後端回 401
 * `{code:'unauthenticated'}`。UI 不該再叫使用者貼任何 token：只提示「請重新登入」
 * 並導回登入頁，所以 `http-client` 只發這個通知，由 `SessionBar` 顯示按鈕。
 * ========================================================================== */
const sessionExpiredListeners = new Set();
/** 訂閱「登入過期／被撤銷」的通知；回傳取消訂閱函式。 */
export function onSessionExpired(listener) {
    sessionExpiredListeners.add(listener);
    return () => {
        sessionExpiredListeners.delete(listener);
    };
}
/** 由 `http-client` 在 `/api/*` 回 401 時呼叫。 */
export function notifySessionExpired(message) {
    for (const listener of sessionExpiredListeners)
        listener(message);
}
/**
 * 通知「寫入被擋下來，缺的是操作金鑰（X-Api-Token），不是登入」。
 *
 * 為什麼要跟 `notifySessionExpired` 分開：那條通道會讓 `SessionBar` 長出「重新登入」按鈕，
 * 而缺金鑰的人**剛登入**，按幾次重新登入都不會好。這一條由「操作金鑰」設定畫面訂閱
 * （`apps/console/src/auth/ApiTokenSettings.tsx`），收到就把設定框叫出來。
 */
const apiTokenListeners = new Set();
/** 訂閱「需要操作金鑰」的通知；回傳取消訂閱函式。 */
export function onApiTokenRequired(listener) {
    apiTokenListeners.add(listener);
    return () => {
        apiTokenListeners.delete(listener);
    };
}
/** 由 `http-client` 在「後端說缺 X-Api-Token」時呼叫（不是 session 過期）。 */
export function notifyApiTokenRequired(message) {
    for (const listener of apiTokenListeners)
        listener(message);
}
/**
 * 這個 client 打 admin 端點需不需要 token？
 *
 * mock 不需要（demo 站不該跳出輸入列），`createHttpApiClient` 建構時會呼叫
 * `markAdminAuthRequired()`。AdminShell 沒有 client 物件可用（`App.tsx` 才有），
 * 所以用這個模組層旗標代替傳 prop。
 */
let adminAuthRequired = false;
/** 由 `createHttpApiClient` 呼叫；只有 http 模式會變成 true（mock 不會把它改回 false）。 */
export function markAdminAuthRequired() {
    adminAuthRequired = true;
}
/** AdminShell 用它決定要不要顯示 token 輸入列。 */
export function isAdminAuthRequired() {
    return adminAuthRequired;
}
const adminAuthListeners = new Set();
/**
 * 訂閱「admin 請求被拒（401／403）」的通知；回傳取消訂閱函式。
 * 上層（`AdminShell` 的 token 列）靠它把輸入框重新叫出來，不必輪詢。
 */
export function onAdminUnauthorized(listener) {
    adminAuthListeners.add(listener);
    return () => {
        adminAuthListeners.delete(listener);
    };
}
/** 由 `http-client` 在收到 401／403 時呼叫（也用在「根本沒帶 token 就不打」的情況）。 */
export function notifyAdminUnauthorized(message) {
    for (const listener of adminAuthListeners)
        listener(message);
}
//# sourceMappingURL=client.js.map